// @flow

import * as React from 'react';

import { Repeat } from 'lucide-react';
import { useParams } from 'react-router-dom';

import type { DecoratedProduct } from '@gandi/cart';
import { useTranslator } from '@gandi/translate';

import type { InstanceSize } from 'src/modules/simplehosting/prices';

import { useWebhostingPlanPrices } from '../modules/webhosting';
import type { Options, Option } from '../modules/webhosting';

import { PlanRowWithOptions } from './PlanRowWithOptions';
import { PlanRows } from './PlanRows';

export type Props = {
  disabled: boolean,
  onChange: (evt: SyntheticInputEvent<>) => Promise<void>,
  options?: Options,
  product: DecoratedProduct,
};

// Export for the unit tests
export function compareLastElement(
  products?: Options,
  targetObject: Option,
): boolean {
  if (!products || products.length === 0) return false;

  const lastElement = products[products.length - 1];
  return lastElement.value === targetObject.value;
}

function useVisiblePlans(
  options?: Options,
  selectedSize: InstanceSize,
): Options {
  const { cartId } = useParams<{ cartId: string }>();
  const [visiblePlans, setVisiblePlans] = React.useState<Options>([]);
  const cartIdRef = React.useRef(cartId);

  React.useEffect(() => {
    const shouldUpdatePlans =
      cartId !== cartIdRef.current ||
      (!!options?.length && !visiblePlans.length);

    if (shouldUpdatePlans) {
      cartIdRef.current = cartId;

      const newVisiblePlans: Options = [];

      if (options?.length) {
        const selectedIndex = options.findIndex(
          plan => plan.value === selectedSize,
        );

        if (selectedIndex !== -1) {
          newVisiblePlans.push(options[selectedIndex]);

          if (selectedIndex < options.length - 1) {
            newVisiblePlans.push(options[selectedIndex + 1]);
          }
        }
      }

      setVisiblePlans(newVisiblePlans);
    }
  }, [cartId, options, selectedSize, visiblePlans.length]);

  return visiblePlans;
}

export function WebHostingPlanRows({
  disabled,
  onChange,
  options,
  product,
}: Props): React.MixedElement {
  const { __ } = useTranslator();

  // $FlowFixMe[incompatible-type] Fix in Cart lib
  const selectedSize: InstanceSize = product.payload.size || 'starter';

  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const visiblePlans = useVisiblePlans(options, selectedSize);

  const handlePlanSelect = (e: SyntheticInputEvent<>) => {
    setIsSubmitting(true);
    onChange(e).finally(() => setIsSubmitting(false));
  };

  const { formattedDiffMonthly, firstPrice, pricesByPlanByDuration } =
    useWebhostingPlanPrices(
      product?.payload.duration || 1,
      visiblePlans,
      product.process,
    ) || {};

  return (
    <PlanRows ariaLabel="webHostingSize">
      {!!visiblePlans?.length &&
        visiblePlans.map(option => (
          <PlanRowWithOptions
            key={option.value}
            isSelected={option.value === selectedSize}
            onSelect={handlePlanSelect}
            disabled={disabled || isSubmitting}
            value={option.value}
            price={
              (compareLastElement(visiblePlans, option) &&
                formattedDiffMonthly) ||
              firstPrice
            }
            detail={
              product.payload.duration !== 12 &&
              !compareLastElement(options, option) &&
              pricesByPlanByDuration &&
              pricesByPlanByDuration[option.value] && (
                <>
                  <span>
                    {__('Pay yearly and save %(diffYearly)s', {
                      diffYearly: pricesByPlanByDuration[option.value],
                    })}
                  </span>
                  <Repeat size={12} />
                </>
              )
            }
            hideMailboxOption={!product.payload.domain?.fqdn}
          />
        ))}
    </PlanRows>
  );
}
